import { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, Search, Calendar } from "lucide-react";
import { contractService } from "../services/contractService";
import { toast } from "sonner";

const Planning = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState("");
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadWeekContracts();
  }, [currentDate]);

  const loadWeekContracts = async () => {
    setLoading(true);
    try {
      const weekStart = new Date(currentDate);
      const day = weekStart.getDay();
      const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
      weekStart.setDate(diff);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekEndInclusive = new Date(weekEnd);
      weekEndInclusive.setDate(weekEndInclusive.getDate() + 1);

      const data = await contractService.getContractsByWeek(weekStart, weekEnd);
      
      const parseDate = (dateStr) => {
        if (!dateStr) return null;
        if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
          const year = parseInt(dateStr.substring(0, 4));
          const month = parseInt(dateStr.substring(4, 6));
          const day = parseInt(dateStr.substring(6, 8));
          return new Date(year, month - 1, day);
        }
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) return date;
        const parts = dateStr.split('/');
        if (parts.length === 3) return new Date(parts[2], parts[1] - 1, parts[0]);
        return null;
      };

      const transformedMissions = [];
      (data || []).forEach((contract) => {
        const dateDebut = parseDate(contract.DateDebut);
        const dateFin = parseDate(contract.DateFin);
        if (!dateDebut || isNaN(dateDebut.getTime())) return;
        const endDate = dateFin && !isNaN(dateFin.getTime()) ? dateFin : dateDebut;
        let currentDate = new Date(dateDebut);
        while (currentDate <= endDate) {
          if (currentDate >= weekStart && currentDate < weekEndInclusive) {
            transformedMissions.push({
              ...contract,
              id: contract._id + "_" + currentDate.toISOString().split('T')[0],
              interim: contract.NomPrenom || "N/A",
              client: contract.RaisonSociale || "N/A",
              date: new Date(currentDate),
              location: contract.Ville || contract.AdresseVille || "N/A",
              typeContrat: contract.TypeContrat || "Intérim",
            });
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
      });
      setMissions(transformedMissions);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors du chargement du planning");
    } finally {
      setLoading(false);
    }
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      return date;
    });
  };

  const weekDays = getWeekDays();
  const dayNames = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

  const filteredMissions = useMemo(() => {
    return missions.filter(m =>
      m.interim.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.client.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [missions, searchTerm]);

  const getMissionsForDate = (date) => {
    return filteredMissions.filter(m => m.date.toDateString() === date.toDateString());
  };

  const weekStart = new Date(currentDate);
  const day = weekStart.getDay();
  const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
  weekStart.setDate(diff);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  return (
    <div className="min-h-screen">
      <div className="container mx-auto py-8 px-4">
        {/* En-tête moderne */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg">
              <Calendar size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r pb-2 from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Planning Hebdomadaire
              </h1>
              <p className="text-slate-600 mt-1">
                Semaine du <span className="font-semibold text-slate-800">{weekStart.toLocaleDateString("fr-FR")}</span> au <span className="font-semibold text-slate-800">{weekEnd.toLocaleDateString("fr-FR")}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Barre de navigation et recherche */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getTime() - 7*24*60*60*1000))}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getTime() + 7*24*60*60*1000))}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
            >
              <ChevronRight size={24} />
            </button>
            <div className="h-6 w-px bg-slate-300"></div>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par intérimaire"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-4">
            {weekDays.map((date, idx) => {
              const dayMissions = getMissionsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <div
                  key={idx}
                  className={`bg-white rounded-xl shadow-sm border ${isToday ? "border-blue-500 border-2" : "border-slate-200"} overflow-hidden flex flex-col min-h-[500px] hover:shadow-md transition-all duration-300`}
                >
                  <div className={`p-4 border-b ${isToday ? "bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}>
                    <p className={`font-semibold ${isToday ? "text-blue-600" : "text-slate-800"}`}>{dayNames[idx]}</p>
                    <p className={`text-lg font-bold ${isToday ? "text-blue-600" : "text-slate-900"}`}>{date.getDate()}</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {dayMissions.length === 0 ? (
                      <div className="text-center text-slate-400 py-12">
                        <p className="text-sm">Aucune mission</p>
                      </div>
                    ) : (
                      dayMissions.map((mission) => (
                        <div
                          key={mission.id}
                          className="bg-slate-50 p-3 rounded-lg border border-slate-200 hover:shadow-md hover:border-blue-300 transition-all duration-300"
                        >
                          <p className="font-semibold text-slate-800 text-sm truncate">{mission.interim}</p>
                          {mission.typeContrat && mission.typeContrat !== "N/A" && (
                            <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mb-2">{mission.typeContrat}</span>
                          )}
                          {mission.location && mission.location !== "N/A" && (
                            <p className="text-xs text-slate-600">📍 {mission.location}</p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Planning;
